import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar, Clock, CheckCircle, XCircle, AlertCircle, Zap, FileText, Brain, Target, RefreshCw } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { useUser } from '@/context/UserContext'
import { getAxiosInstance } from '@/network/axiosInstance'

interface AIActionLog {
  id: string
  actionType: string
  startedAt: string
  completedAt?: string
  success?: boolean
  duration?: number
  metadata?: {
    documentType?: string
    processingTime?: number
    projectId?: string
    projectName?: string
    nodeType?: string
    inputSize?: number
    outputSize?: number
    errorMessage?: string
  }
}

interface AIActionLogsViewerProps {
  projectId?: string
  organizationId?: string
  nodeId?: string
  isEmbedded?: boolean
}

const AIActionLogsViewer: React.FC<AIActionLogsViewerProps> = ({
  projectId,
  organizationId,
  nodeId,
  isEmbedded = false
}) => {
  const user = useUser()
  const { toast } = useToast()
  const [logs, setLogs] = useState<AIActionLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState('all') // all, 24h, 7d, 30d, 90d
  const [actionTypeFilter, setActionTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all') // all, success, failed, pending

  const fetchActionLogs = async () => {
    if (!user?.id) return    setLoading(true)
    setError(null)

    try {
      const axiosInstance = getAxiosInstance()
      const params: any = {
        range: dateRange !== 'all' ? dateRange : undefined,
        actionType: actionTypeFilter !== 'all' ? actionTypeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined
      }

      if (projectId) {
        params.projectId = projectId
      }
      if (organizationId) {
        params.organizationId = organizationId
      }

      if (nodeId) {
        params.nodeId = nodeId
      }

      const response = await axiosInstance.get('/api/subscriptions/action-logs', { params })
      setLogs(response.data.logs || [])
    } catch (err) {
      console.error('Error fetching action logs:', err)
      setError('Failed to load action logs')
      toast({
        title: 'Error',
        description: 'Failed to load AI action logs',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActionLogs()
  }, [user?.id, projectId, organizationId, dateRange, actionTypeFilter, statusFilter])

  const getActionIcon = (actionType: string) => {
    switch (actionType.toLowerCase()) {
      case 'document_analysis':
      case 'document_processing':
        return <FileText className="h-4 w-4" />
      case 'content_generation':
      case 'content_crafting':
        return <Brain className="h-4 w-4" />
      case 'strategy_analysis':
      case 'strategy_generation':
        return <Target className="h-4 w-4" />
      case 'title_generation':
      case 'title_update':
        return <Zap className="h-4 w-4" />
      default:
        return <Zap className="h-4 w-4" />
    }
  }

  const getStatusIcon = (log: AIActionLog) => {
    if (log.completedAt) {
      return log.success ? (
        <CheckCircle className="h-4 w-4 text-green-500" />
      ) : (
        <XCircle className="h-4 w-4 text-red-500" />
      )
    }
    return <AlertCircle className="h-4 w-4 text-yellow-500" />
  }

  const getStatusText = (log: AIActionLog) => {
    if (log.completedAt) {
      return log.success ? 'Completed' : 'Failed'
    }
    return 'In Progress'
  }

  const getStatusColor = (log: AIActionLog) => {
    if (log.completedAt) {
      return log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }
    return 'bg-yellow-100 text-yellow-800'
  }

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt)
    const end = completedAt ? new Date(completedAt) : new Date()
    const durationMs = end.getTime() - start.getTime()
    
    if (durationMs < 1000) return `${durationMs}ms`
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`
    return `${(durationMs / 60000).toFixed(1)}m`
  }

  const formatActionType = (actionType: string) => {
    return actionType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getActionTypeOptions = () => {
    const types = [...new Set(logs.map(log => log.actionType))]
    return types.map(type => ({
      value: type,
      label: formatActionType(type)
    }))
  }

  if (loading) {
    return (
      <Card>        <CardHeader className="" hasCardAction={false}>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Action Logs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <RefreshCw className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    )
  }
  const content = (
    <>
      {!isEmbedded && (
        <CardHeader className="" hasCardAction={false}>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            AI Action Logs
            {projectId && <Badge variant="outline">Project View</Badge>}
            {organizationId && <Badge variant="outline">Organization View</Badge>}
          </CardTitle>
          <CardDescription>
            {projectId 
              ? 'AI actions performed in this project'
              : organizationId 
                ? 'AI actions performed by organization members'
                : 'Your recent AI actions and their status'
            }
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className={isEmbedded ? "p-0" : ""}>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="24h">Last 24h</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Action Types</SelectItem>
              {getActionTypeOptions().map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="pending">In Progress</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={fetchActionLogs}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {error ? (
          <div className="text-center py-8">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">{error}</p>
            <Button onClick={fetchActionLogs} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8">
            <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">No AI actions found</p>
            <p className="text-sm text-gray-500">
              {projectId 
                ? 'No AI actions have been performed in this project yet'
                : 'Start using AI features to see your action history here'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log) => (
              <div
                key={log.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {getActionIcon(log.actionType)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-medium">
                          {formatActionType(log.actionType)}
                        </h4>
                        <Badge className={getStatusColor(log)}>
                          {getStatusText(log)}
                        </Badge>
                        {log.metadata?.projectName && !projectId && (
                          <Badge variant="outline" className="text-xs">
                            {log.metadata.projectName}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            {new Date(log.startedAt).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>Duration:</span>
                          <span>{formatDuration(log.startedAt, log.completedAt)}</span>
                        </div>
                        {log.metadata?.inputSize && (
                          <div className="flex items-center gap-1">
                            <span>Input:</span>
                            <span>{log.metadata.inputSize} chars</span>
                          </div>
                        )}
                      </div>

                      {log.metadata?.errorMessage && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                          <strong>Error:</strong> {log.metadata.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(log)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {logs.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {logs.length}
                </div>
                <div className="text-sm text-gray-600">Total Actions</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {logs.filter(log => log.success).length}
                </div>
                <div className="text-sm text-gray-600">Successful</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {logs.filter(log => log.success === false).length}
                </div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">
                  {logs.filter(log => !log.completedAt).length}
                </div>
                <div className="text-sm text-gray-600">In Progress</div>
              </div>
            </div>
          </div>        )}
      </CardContent>
    </>
  );

  return isEmbedded ? content : <Card>{content}</Card>;
}

export default AIActionLogsViewer
